import { motion } from "motion/react";

import img1 from "../assets/LifeAtFlowstep/1.jpg";
import img2 from "../assets/LifeAtFlowstep/2.jpg";
import img3 from "../assets/LifeAtFlowstep/3.jpg";
import img4 from "../assets/LifeAtFlowstep/4.jpg";
import img5 from "../assets/LifeAtFlowstep/5.jpg";
import img6 from "../assets/LifeAtFlowstep/6.jpg";
import img7 from "../assets/LifeAtFlowstep/7.jpg";
import img8 from "../assets/LifeAtFlowstep/8.jpg";

import { useIsMobile } from "./ui/use-mobile";
import { useReducedMotion } from "./ui/use-reduced-motion";

interface TeamPhoto {
  id: string;
  image: string;
  alt: string;
  width: string;
  height: string;
  position: {
    top?: string;
    left?: string;
    right?: string;
    bottom?: string;
  };
  rotation: number;
  zIndex: number;
}

const teamPhotos: TeamPhoto[] = [
  {
    id: "1",
    image: img1,
    alt: "Team cooking event",
    width: "380px",
    height: "260px",
    position: { top: "5%", left: "5%" },
    rotation: -6,
    zIndex: 3,
  },
  {
    id: "2",
    image: img2,
    alt: "Pizza lunch in office",
    width: "340px",
    height: "240px",
    position: { top: "42%", left: "8%" },
    rotation: 5,
    zIndex: 5,
  },
  {
    id: "3",
    image: img3,
    alt: "Collaborative work session",
    width: "400px",
    height: "280px",
    position: { top: "58%", right: "7%" },
    rotation: -8,
    zIndex: 2,
  },
  {
    id: "4",
    image: img4,
    alt: "Team nature hike",
    width: "280px",
    height: "360px",
    position: { top: "3%", left: "33%" },
    rotation: 8,
    zIndex: 4,
  },
  {
    id: "5",
    image: img5,
    alt: "Sauna evening retreat",
    width: "320px",
    height: "220px",
    position: { top: "8%", right: "5%" },
    rotation: -4,
    zIndex: 6,
  },
  {
    id: "6",
    image: img6,
    alt: "Office presentation",
    width: "360px",
    height: "250px",
    position: { top: "53%", left: "36%" },
    rotation: 2,
    zIndex: 1,
  },
  {
    id: "7",
    image: img7,
    alt: "Colorful donuts",
    width: "260px",
    height: "260px",
    position: { top: "25%", left: "55%" },
    rotation: -9,
    zIndex: 7,
  },
  {
    id: "8",
    image: img8,
    alt: "Team outing",
    width: "300px",
    height: "200px",
    position: { top: "70%", left: "15%" },
    rotation: -6,
    zIndex: 8,
  },
];

export default function LifeAtFlowstep() {
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();

  return (
    <section className="w-full overflow-hidden py-12 lg:py-32">
      <div className="mx-auto mb-8 max-w-[1600px] px-4 md:px-8 lg:mb-16 lg:px-16">
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 0.8, ease: "easeOut" }
          }
          className="space-y-4 md:space-y-6"
        >
          <h2
            className="leading-[1.1] tracking-tight text-white"
            style={{ fontSize: "var(--text-section)", fontWeight: "600" }}
          >
            Life at Flowstep
          </h2>
          <p
            className="max-w-2xl leading-relaxed text-gray-300"
            style={{ fontSize: "var(--text-lead)" }}
          >
            We keep the team small and the standards high. We work with urgency,
            care deeply about the details, and build things that matter. And
            yes—some fun along the way.
          </p>
        </motion.div>
      </div>

      {/* Mobile: Continuous carousel */}
      {isMobile ? (
        <div className="relative overflow-hidden">
          <motion.div
            className="flex gap-4"
            animate={{
              x: [0, -(300 * teamPhotos.length + 16 * teamPhotos.length)],
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: 35,
                ease: "linear",
              },
            }}
          >
            {/* Render photos twice for seamless loop */}
            {[...teamPhotos, ...teamPhotos].map((photo, index) => (
              <div
                key={`${photo.id}-${index}`}
                className="flex-shrink-0"
                style={{ width: 300 }}
              >
                <div
                  className="relative w-full overflow-hidden rounded-2xl bg-white p-2"
                  style={{
                    height: 220,
                    boxShadow: `
                      0 1px 2px rgba(0,0,0,0.06),
                      0 4px 8px rgba(0,0,0,0.08),
                      0 12px 24px rgba(0,0,0,0.12)
                    `,
                  }}
                >
                  <div className="relative h-full w-full overflow-hidden rounded-lg">
                    <img
                      src={photo.image}
                      alt={photo.alt}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      ) : (
        // Desktop: Playful scattered photo collage
        <div className="relative mx-auto h-[800px] w-full max-w-[1400px] px-4 md:px-8 lg:px-16">
          {teamPhotos.map((photo, index) => (
            <motion.div
              key={photo.id}
              initial={
                prefersReducedMotion
                  ? { opacity: 1, scale: 1, rotate: photo.rotation }
                  : { opacity: 0, scale: 0.8, rotate: photo.rotation - 10 }
              }
              whileInView={
                prefersReducedMotion
                  ? { opacity: 1, scale: 1, rotate: photo.rotation }
                  : { opacity: 1, scale: 1, rotate: photo.rotation }
              }
              viewport={{ once: true, margin: "-100px" }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : {
                      duration: 0.8,
                      delay: index * 0.15,
                      ease: [0.34, 1.56, 0.64, 1],
                    }
              }
              whileHover={
                prefersReducedMotion
                  ? {}
                  : {
                      scale: 1.05,
                      rotate: photo.rotation + 2,
                      zIndex: 10,
                      transition: { duration: 0.3 },
                    }
              }
              className="absolute cursor-pointer"
              style={{
                ...photo.position,
                width: photo.width,
                height: photo.height,
                zIndex: photo.zIndex,
              }}
            >
              <div
                className="relative h-full w-full overflow-hidden rounded-2xl bg-white p-3"
                style={{
                  boxShadow: `
                    0 1px 2px rgba(0,0,0,0.06),
                    0 4px 8px rgba(0,0,0,0.08),
                    0 12px 24px rgba(0,0,0,0.12),
                    0 20px 40px rgba(0,0,0,0.08)
                  `,
                }}
              >
                <div className="relative h-full w-full overflow-hidden rounded-lg">
                  <img
                    src={photo.image}
                    alt={photo.alt}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}
